// /apps/patient/src/pages/programs/ProgramDetail.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  Calendar,
  Clock,
  PlayCircle,
  CheckCircle,
  Target,
  TrendingUp,
  Info,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Video,
  Heart,
  Zap,
  Users,
  FileText,
  AlertCircle,
  Repeat,
  RefreshCw
} from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { patientApiService } from '../../services/api';
import { DaySwapModal } from '../../components/programs/DaySwapModal';
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isToday,
  isPast,
  isFuture,
  addWeeks,
  differenceInDays,
  startOfDay,
  getISODay
} from 'date-fns';
import { CardioConfig, PatientScheduleOverride } from '@prism-ehr/shared';

// Helper functions for day conversion
const jsToISODay = (jsDay: number): number => jsDay === 0 ? 7 : jsDay;
const isoDayToJS = (isoDay: number): number => isoDay === 7 ? 0 : isoDay;

// ISO day names lookup (1=Monday, 7=Sunday)
const ISO_DAY_NAMES: { [key: number]: string } = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday'
};

function isEmbeddedVideo(url: string): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return host === 'youtube.com'
      || host === 'm.youtube.com'
      || host === 'youtu.be'
      || host === 'vimeo.com'
      || host === 'player.vimeo.com';
  } catch {
    return false;
  }
}

function toEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === 'vimeo.com') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  } catch {
    return url;
  }
}

function ExerciseDetailRow({ exercise, index }: { exercise: any; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const imageUrl = exercise.exercise_gif || exercise.image_url;
  const params: string[] = [];
  if (exercise.parameters?.sets) params.push(`${exercise.parameters.sets} sets`);
  if (exercise.parameters?.reps) params.push(`× ${exercise.parameters.reps} reps`);
  if (exercise.parameters?.weight) params.push(`@ ${exercise.parameters.weight}`);
  if (exercise.parameters?.hold_time) params.push(`Hold ${exercise.parameters.hold_time}s`);
  if (exercise.parameters?.rest_time) params.push(`Rest ${exercise.parameters.rest_time}s`);
  if (exercise.parameters?.duration_seconds) params.push(`${exercise.parameters.duration_seconds}s`);
  if (exercise.parameters?.frequency) params.push(exercise.parameters.frequency);

  return (
    <div className="py-2 border-b last:border-0">
      <div
        className="flex items-center space-x-3 cursor-pointer"
        onClick={() => setExpanded(prev => !prev)}
      >
        <div className="flex-shrink-0">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={exercise.official_title || 'Exercise'}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <Dumbbell className="h-5 w-5 text-gray-500" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">
            {exercise.official_title || exercise.display_name || `Exercise ${index + 1}`}
          </p>
          {params.length > 0 && (
            <p className="text-sm text-gray-600">{params.join(' · ')}</p>
          )}
          {exercise.set_type && exercise.set_type !== 'standard' && (
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded mt-1 inline-block">
              {exercise.set_type}
            </span>
          )}
        </div>

        <div className="flex-shrink-0">
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 ml-13">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={exercise.official_title || 'Exercise'}
              className="w-full rounded-lg mb-3"
            />
          )}

          {exercise.video_url && (
            <div className="mb-3">
              <button
                type="button"
                onClick={() => setShowVideo(v => !v)}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                {showVideo ? 'Hide Video' : 'Watch Video'}
              </button>
              {showVideo && (
                <div className="mt-2">
                  {isEmbeddedVideo(exercise.video_url) ? (
                    <div className="relative aspect-video">
                      <iframe
                        src={toEmbedUrl(exercise.video_url)}
                        title={`${exercise.official_title} video`}
                        className="absolute inset-0 w-full h-full rounded-lg"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <video
                      key={exercise.video_url}
                      src={exercise.video_url}
                      controls
                      className="w-full rounded-lg"
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {exercise.instructions && (
            <div className="text-sm text-gray-600 mb-3">
              {exercise.instructions.split('\n').map((line: string, i: number) => {
                const isBullet = line.trim().startsWith('-') || line.trim().startsWith('•');
                if (isBullet) {
                  return <li key={i} className="ml-4">{line.replace(/^[-•]\s*/, '')}</li>;
                }
                return line.trim() ? <p key={i} className="mb-1">{line}</p> : null;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ProgramDetails {
  assignment: any;
  program: any; // WorkoutPlan or RehabProgram
  currentSchedule: any[]; // Current week/phase prescriptions
  progress: any;
  completionHistory: any[];
}

interface CardioExerciseDisplayProps {
  exercise: any;
  index: number;
}

interface ExerciseWithDetails {
  exercise_id: string;
  exercise?: any; // Full exercise details
  sequence: number;
  parameters: any;
  set_type?: string;
  group_id?: string;
}

const CardioExerciseDisplay: React.FC<CardioExerciseDisplayProps> = ({ exercise, index }) => {
  const config: CardioConfig = exercise.parameters?.cardio_config || {
    format: 'simple',
    simple: {
      duration_minutes: exercise.parameters?.duration_minutes || 20,
      intensity: 'moderate'
    }
  };

  const getCardioSummary = () => {
    if (config.format === 'simple' && config.simple) {
      const parts = [];
      if (config.simple.duration_minutes) {
        parts.push(`${config.simple.duration_minutes} min`);
      }
      if (config.simple.distance) {
        parts.push(`${config.simple.distance} ${config.simple.distance_unit || 'miles'}`);
      }
      if (config.simple.intensity) {
        parts.push(`@ ${config.simple.intensity}`);
      }
      if (config.simple.target_pace) {
        parts.push(`(${config.simple.target_pace})`);
      }
      return parts.join(' ');
    } else if (config.format === 'intervals' && config.intervals) {
      const totalSegments = config.intervals.segments.length;
      const totalRounds = config.intervals.repeat_count || 1;
      const totalDuration = config.intervals.segments.reduce(
        (sum, seg) => sum + (seg.duration_seconds || 0), 0
      ) * totalRounds;
      return `${totalRounds} rounds × ${totalSegments} intervals (${Math.round(totalDuration / 60)} min total)`;
    } else if (config.format === 'structured' && config.structured) {
      const totalDuration = config.structured.segments.reduce(
        (sum, seg) => sum + (seg.duration_seconds || 0), 0
      );
      return `${config.structured.segments.length} segments (${Math.round(totalDuration / 60)} min total)`;
    }
    return 'Cardio';
  };

  const renderIntervalBreakdown = () => {
    if (config.format !== 'intervals' || !config.intervals) return null;

    return (
      <div className="mt-2 ml-12 bg-blue-50 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <Repeat className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            Repeat {config.intervals.repeat_count}x:
          </span>
        </div>
        <div className="space-y-1">
          {config.intervals.segments.map((segment, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <span className="text-blue-800 capitalize">
                {idx + 1}. {segment.type}
              </span>
              <span className="text-blue-600">
                {segment.duration_seconds}s @ {segment.intensity || 'moderate'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStructuredBreakdown = () => {
    if (config.format !== 'structured' || !config.structured) return null;

    return (
      <div className="mt-2 ml-12 bg-gray-50 rounded-lg p-3">
        <div className="space-y-1">
          {config.structured.segments.map((segment, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <span className="text-gray-700 capitalize">
                {idx + 1}. {segment.type}
              </span>
              <span className="text-gray-600">
                {Math.floor((segment.duration_seconds || 0) / 60)}:
                {((segment.duration_seconds || 0) % 60).toString().padStart(2, '0')}
                @ {segment.intensity || 'moderate'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="border-b last:border-0 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1">
          {/* Cardio Icon */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Activity className="h-5 w-5 text-blue-600" />
            </div>
          </div>

          <div className="flex-1">
            <p className="font-medium text-gray-900">
              {exercise.official_title || exercise.display_name || 'Cardio Activity'}
            </p>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Clock className="h-3 w-3" />
              {getCardioSummary()}
            </p>
          </div>
        </div>

        {config.format === 'intervals' && (
          <div className="flex items-center text-blue-600">
            <TrendingUp className="h-4 w-4" />
          </div>
        )}
      </div>

      {/* Show breakdown for intervals and structured workouts */}
      {renderIntervalBreakdown()}
      {renderStructuredBreakdown()}
    </div>
  );
};

const isCardioExercise = (exercise: any): boolean => {
  return exercise?.is_cardio_activity === true ||
    exercise?.exercise_type === 'cardio' ||
    exercise?.exercise_id?.startsWith('cardio_');
}


export function ProgramDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState<ProgramDetails | null>(null);
  const [currentView, setCurrentView] = useState<'overview' | 'schedule' | 'exercises'>('overview');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedWorkouts, setExpandedWorkouts] = useState<Set<string>>(new Set());
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedPhase, setSelectedPhase] = useState(1);
  const [completedDates, setCompletedDates] = useState<Set<string>>(new Set());

  // Day swap modal state
  const [showDaySwapModal, setShowDaySwapModal] = useState(false);
  const [swapTargetDay, setSwapTargetDay] = useState<number | null>(null);
  const [swapTargetWorkoutName, setSwapTargetWorkoutName] = useState<string>('');
  const [planAction, setPlanAction] = useState<'regenerate' | 'advance' | null>(null);
  const [planActionError, setPlanActionError] = useState<string | null>(null);
  const [isResettingSchedule, setIsResettingSchedule] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProgramDetails();
    }
  }, [id]);

  const fetchProgramDetails = async () => {
    setLoading(true);
    try {
      // Get the assignment
      const assignment = await patientApiService.getExerciseAssignment(id!);

      // Get progress with better error handling
      const progress = await patientApiService.getAssignmentProgress(id!).catch(() => {
        return {
          current_week: 1,
          current_phase: 1,
          sessions_completed: 0,
          sessions_scheduled: 0,
          completion_percentage: 0
        };
      });

      let program = null;
      let currentSchedule = [];

      // Fetch program-specific details
      if (assignment.assignment_type === 'workout_plan' && assignment.workout_plan_id) {
        program = await patientApiService.getWorkoutPlan(assignment.workout_plan_id);
        const currentWeek = progress?.current_week || 1;
        setSelectedWeek(currentWeek);
        currentSchedule = await patientApiService.getWorkoutWeekSchedule(
          assignment.workout_plan_id,
          currentWeek
        );
      } else if (assignment.assignment_type === 'rehab_program' && assignment.rehab_program_id) {
        program = await patientApiService.getRehabProgram(assignment.rehab_program_id);
        const currentPhase = progress?.current_phase || 1;
        setSelectedPhase(currentPhase);
        currentSchedule = await patientApiService.getRehabPhaseSchedule(
          assignment.rehab_program_id,
          currentPhase
        );
      } else if (assignment.assignment_type === 'single_prescription' && assignment.exercise_prescription_id) {
        program = await patientApiService.getExerciseProgram(assignment.exercise_prescription_id);
        currentSchedule = [{ exercise_prescription: program, frequency_per_week: program.schedule.recommended_frequency }];
      }

      const sessions = await patientApiService.getAssignmentSessions(id!).catch(() => []);
      const completedDateSet = new Set<string>();
      if (Array.isArray(sessions)) {
        sessions.forEach((session: any) => {
          if (session.status === 'completed') {
            completedDateSet.add(session.session_date);
          }
        });
      }
      setCompletedDates(completedDateSet);

      setProgramData({
        assignment,
        program,
        currentSchedule,
        progress,
        completionHistory: [] // Keep for interface compatibility
      });
    } catch (error) {
      console.error('Error fetching program details:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(moduleId)) {
        newExpanded.delete(moduleId);
      } else {
        newExpanded.add(moduleId);
      }
      return newExpanded;
    });
  };

  const toggleWorkout = (workoutId: string) => {
    setExpandedWorkouts(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(workoutId)) {
        newExpanded.delete(workoutId);
      } else {
        newExpanded.add(workoutId);
      }
      return newExpanded;
    });
  };

  const getModuleIcon = (moduleType: string) => {
    switch (moduleType) {
      case 'warmup': return Heart;
      case 'activation': return Zap;
      case 'main_workout': return Dumbbell;
      case 'cooldown': return Heart;
      case 'stretching': return Activity;
      case 'cardio': return TrendingUp;
      default: return Activity;
    }
  };

  const getWorkoutTypeIcon = (workoutType: string) => {
    switch (workoutType?.toLowerCase()) {
      case 'strength': return Dumbbell;
      case 'cardio': return TrendingUp;
      case 'hiit': return Zap;
      case 'flexibility': return Activity;
      case 'recovery': return Heart;
      default: return Dumbbell;
    }
  };

  // Apply schedule overrides to the current schedule
  const applyScheduleOverrides = (schedule: any[], weekNum: number): any[] => {
    const overrides: PatientScheduleOverride[] = programData?.assignment?.schedule_overrides || [];
    if (overrides.length === 0) return schedule;

    return schedule.map(item => {
      const override = overrides.find(
        o => o.week_number === weekNum && o.original_day === item.day_of_week
      );
      return override
        ? { ...item, day_of_week: override.new_day, _swapped: true, _original_day: item.day_of_week }
        : item;
    });
  };

  const getScheduleDays = () => {
    if (!programData) return [];

    if (programData.assignment.assignment_type === 'workout_plan') {
      // For workout plans, use day_of_week from schedule (ISO format: 1=Mon, 7=Sun)
      // Apply any schedule overrides
      const scheduleWithOverrides = applyScheduleOverrides(programData.currentSchedule, selectedWeek);
      return scheduleWithOverrides.map(item => ({
        day: item.day_of_week,
        prescription: item.exercise_prescription,
        optional: item.is_optional,
        _swapped: item._swapped,
        _original_day: item._original_day
      }));
    } else {
      // For rehab programs, distribute based on frequency
      const frequency = programData.currentSchedule[0]?.frequency_per_week || 3;
      const dayMapping: { [key: string]: number[] } = {
        '1x_week': [3], // Wednesday
        '2x_week': [2, 4], // Tuesday, Thursday
        '3x_week': [1, 3, 5], // Monday, Wednesday, Friday
        '4x_week': [1, 2, 4, 5],
        '5x_week': [1, 2, 3, 4, 5],
        '6x_week': [1, 2, 3, 4, 5, 6],
        '7x_week': [1, 2, 3, 4, 5, 6, 7]
      };

      const days = dayMapping[frequency] || [1, 3, 5];
      return days.map(day => ({
        day,
        prescription: programData.currentSchedule[0]?.exercise_prescription,
        optional: false,
        _swapped: false,
        _original_day: undefined
      }));
    }
  };

  // Handle day swap
  const handleDaySwap = async (visualDay: number, newDay: number) => {
    if (!programData || !id) return;

    const overrides: PatientScheduleOverride[] = programData.assignment?.schedule_overrides || [];

    // Find which workout is currently displayed on visualDay
    // This might be a workout that was moved here via an existing override
    const overrideShowingOnVisualDay = overrides.find(
      o => o.week_number === selectedWeek && o.new_day === visualDay
    );

    // The actual original day of the workout we're moving
    const workoutOriginalDay = overrideShowingOnVisualDay
      ? overrideShowingOnVisualDay.original_day
      : visualDay;

    // Find which workout is currently displayed on the target day (newDay)
    const overrideShowingOnNewDay = overrides.find(
      o => o.week_number === selectedWeek && o.new_day === newDay
    );
    const targetWorkoutOriginalDay = overrideShowingOnNewDay
      ? overrideShowingOnNewDay.original_day
      : newDay;

    // Check if we're moving this workout back to its original position
    const isReturningHome = workoutOriginalDay === newDay;

    // Check if target workout would go back to its original position
    const targetIsReturningHome = targetWorkoutOriginalDay === visualDay;

    if (isReturningHome && targetIsReturningHome) {
      // Both workouts returning to original positions - delete both overrides
      // Delete the override that moved the workout we're dragging
      if (overrideShowingOnVisualDay) {
        await patientApiService.deleteScheduleOverride(id, {
          week_number: selectedWeek,
          original_day: workoutOriginalDay
        });
      }
      // Delete the override that moved the target workout
      if (overrideShowingOnNewDay) {
        await patientApiService.deleteScheduleOverride(id, {
          week_number: selectedWeek,
          original_day: targetWorkoutOriginalDay
        });
      }
      // Also check for any orphaned overrides that were moving things TO these days
      const orphanedOverrideToVisualDay = overrides.find(
        o => o.week_number === selectedWeek && o.new_day === visualDay && o.original_day !== workoutOriginalDay
      );
      if (orphanedOverrideToVisualDay) {
        await patientApiService.deleteScheduleOverride(id, {
          week_number: selectedWeek,
          original_day: orphanedOverrideToVisualDay.original_day
        });
      }
      const orphanedOverrideToNewDay = overrides.find(
        o => o.week_number === selectedWeek && o.new_day === newDay && o.original_day !== targetWorkoutOriginalDay
      );
      if (orphanedOverrideToNewDay) {
        await patientApiService.deleteScheduleOverride(id, {
          week_number: selectedWeek,
          original_day: orphanedOverrideToNewDay.original_day
        });
      }
    } else if (isReturningHome) {
      // Single workout returning home
      // Delete its override
      if (overrideShowingOnVisualDay) {
        await patientApiService.deleteScheduleOverride(id, {
          week_number: selectedWeek,
          original_day: workoutOriginalDay
        });
      }
      // Also delete any override that was moving something TO the original day
      // (which would conflict with this workout returning)
      const conflictingOverride = overrides.find(
        o => o.week_number === selectedWeek && o.new_day === newDay && o.original_day !== workoutOriginalDay
      );
      if (conflictingOverride) {
        await patientApiService.deleteScheduleOverride(id, {
          week_number: selectedWeek,
          original_day: conflictingOverride.original_day
        });
      }
    } else {
      // Regular swap - create new override via API
      // The backend handles creating paired overrides for true swaps
      await patientApiService.createScheduleOverride(id, {
        week_number: selectedWeek,
        original_day: workoutOriginalDay,
        new_day: newDay
      });
    }

    // Refresh program data to get updated overrides
    await fetchProgramDetails();
  };

  // Open day swap modal for a specific day
  const openDaySwapModal = (day: number, workoutName: string) => {
    setSwapTargetDay(day);
    setSwapTargetWorkoutName(workoutName);
    setShowDaySwapModal(true);
  };

  // Get scheduled workouts for the current week (for swap modal)
  const getScheduledWorkoutsForSwapModal = () => {
    const scheduleDays = getScheduleDays();
    return scheduleDays.map(s => ({
      day: s.day,
      prescriptionName: s.prescription?.name || 'Workout',
      isSwapped: s._swapped
    }));
  };

  // Check if there are any schedule overrides for the current week
  const hasScheduleOverrides = (): boolean => {
    const overrides: PatientScheduleOverride[] = programData?.assignment?.schedule_overrides || [];
    return overrides.some(o => o.week_number === selectedWeek);
  };

  // Reset schedule to original (clear all overrides for current week)
  const handleResetSchedule = async () => {
    if (!programData || !id) return;

    setIsResettingSchedule(true);
    try {
      await patientApiService.clearScheduleOverrides(id, selectedWeek);
      await fetchProgramDetails();
    } catch (error) {
      console.error('Error resetting schedule:', error);
    } finally {
      setIsResettingSchedule(false);
    }
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(new Date());
    const weekEnd = endOfWeek(new Date());
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
    const scheduledDays = getScheduleDays();
    const isWorkoutPlan = programData?.assignment.assignment_type === 'workout_plan';

    // Get program start date for boundary check
    const programStartDate = programData?.assignment?.start_date
      ? startOfDay(parseISO(programData.assignment.start_date))
      : null;

    return (
      <div className="grid grid-cols-7 gap-2">
        {days.map((date) => {
          // Convert JS day (0=Sun) to ISO day (1=Mon, 7=Sun) for comparison
          const jsDay = date.getDay();
          const isoDay = jsToISODay(jsDay);

          const scheduled = scheduledDays.find(s => s.day === isoDay);
          const isAfterProgramStart = !programStartDate || startOfDay(date) >= programStartDate;
          const isActiveScheduledDay = scheduled && isAfterProgramStart;
          const dateKey = format(date, 'yyyy-MM-dd');
          const completed = completedDates.has(dateKey);
          const isSwapped = scheduled?._swapped;

          return (
            <div
              key={date.toISOString()}
              className={`
              p-3 rounded-lg text-center transition-all relative
              ${isToday(date) ? 'ring-2 ring-primary' : ''}
              ${isActiveScheduledDay ? (isSwapped ? 'bg-indigo-50 hover:bg-indigo-100' : 'bg-blue-50 hover:bg-blue-100') : 'bg-gray-50'}
              ${completed ? 'bg-green-50' : ''}
              ${isPast(date) && !completed && isActiveScheduledDay ? 'bg-red-50' : ''}
            `}
            >
              {/* Swapped indicator */}
              {isSwapped && (
                <div className="absolute top-1 right-1">
                  <RefreshCw className="h-3 w-3 text-indigo-500" />
                </div>
              )}

              <div
                className="cursor-pointer"
                onClick={() => {
                  if (isActiveScheduledDay) {
                    navigate(`/programs/${id}/session/${format(date, 'yyyy-MM-dd')}`);
                  }
                }}
              >
                <div className="text-xs text-gray-600 mb-1">
                  {format(date, 'EEE')}
                </div>
                <div className="text-sm font-medium">
                  {format(date, 'd')}
                </div>
                {isActiveScheduledDay && (
                  <>
                    {/* Workout type label for workout plans */}
                    {isWorkoutPlan && scheduled.prescription?.name && (
                      <div className="mt-1 text-xs text-gray-700 font-medium truncate px-1" title={scheduled.prescription.name}>
                        {scheduled.prescription.name}
                      </div>
                    )}
                    <div className="mt-2">
                      {completed ? (
                        <CheckCircle className="h-5 w-5 text-green-600 mx-auto" />
                      ) : isToday(date) || isFuture(date) ? (
                        <PlayCircle className="h-5 w-5 text-blue-600 mx-auto" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600 mx-auto" />
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Swap button for workout plans (only for future/today and not completed) */}
              {isWorkoutPlan && isActiveScheduledDay && !completed && (isToday(date) || isFuture(date)) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDaySwapModal(isoDay, scheduled.prescription?.name || 'Workout');
                  }}
                  className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 flex items-center justify-center mx-auto"
                  title="Swap to different day"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Swap
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render workouts list for workout plans (expandable cards showing each day's workout)
  const renderWorkoutsList = () => {
    if (!programData?.currentSchedule || programData.currentSchedule.length === 0) {
      return <p className="text-gray-500">No workouts found for this week.</p>;
    }

    // Apply schedule overrides to show workouts on their swapped days
    const scheduleWithOverrides = applyScheduleOverrides(programData.currentSchedule, selectedWeek);

    return (
      <div className="space-y-4">
        {scheduleWithOverrides.map((scheduleItem: any, index: number) => {
          const prescription = scheduleItem.exercise_prescription;
          if (!prescription) return null;

          const workoutId = `workout-${scheduleItem.id || index}`;
          const isExpanded = expandedWorkouts.has(workoutId);
          const dayName = ISO_DAY_NAMES[scheduleItem.day_of_week] || `Day ${scheduleItem.day_of_week}`;
          const isSwapped = scheduleItem._swapped;
          const WorkoutIcon = getWorkoutTypeIcon(prescription.workout_type);

          // Count total exercises across all modules
          const totalExercises = prescription.modules?.reduce(
            (sum: number, module: any) => sum + (module.exercises?.length || 0), 0
          ) || prescription.exercises?.length || 0;

          return (
            <div key={workoutId} className="bg-white rounded-lg border">
              <button
                onClick={() => toggleWorkout(workoutId)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <WorkoutIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900">{prescription.name || 'Workout'}</h4>
                    <p className="text-sm text-gray-600">
                      {dayName} • {totalExercises} exercises
                      {prescription.estimated_duration_minutes && ` • ${prescription.estimated_duration_minutes} min`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {isSwapped && (
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded flex items-center">
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Swapped
                    </span>
                  )}
                  {scheduleItem.is_optional && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                      Optional
                    </span>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t px-4 py-3 space-y-4">
                  {/* Workout description */}
                  {prescription.description && (
                    <p className="text-sm text-gray-600">{prescription.description}</p>
                  )}

                  {/* Render modules if available */}
                  {prescription.modules && prescription.modules.length > 0 ? (
                    <div className="space-y-3">
                      {prescription.modules.map((module: any) => {
                        const ModuleIcon = getModuleIcon(module.module_type);
                        const moduleId = `${workoutId}-${module.module_id}`;
                        const isModuleExpanded = expandedModules.has(moduleId);

                        return (
                          <div key={moduleId} className="bg-gray-50 rounded-lg">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleModule(moduleId);
                              }}
                              className="w-full p-3 flex items-center justify-between hover:bg-gray-100 rounded-lg"
                            >
                              <div className="flex items-center space-x-2">
                                <ModuleIcon className="h-4 w-4 text-gray-600" />
                                <span className="text-sm font-medium text-gray-700">
                                  {module.name || module.module_name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({module.exercises?.length || 0} exercises)
                                </span>
                              </div>
                              {isModuleExpanded ? (
                                <ChevronUp className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              )}
                            </button>

                            {isModuleExpanded && module.exercises && (
                              <div className="px-3 pb-3">
                                {module.exercises.map((exercise: any, exIndex: number) => (
                                  <ExerciseDetailRow key={exIndex} exercise={exercise} index={exIndex} />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : prescription.exercises && prescription.exercises.length > 0 ? (
                    // Fallback to flat exercise list
                    <div>
                      {prescription.exercises.map((exercise: any, exIndex: number) => (
                        <ExerciseDetailRow key={exIndex} exercise={exercise} index={exIndex} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No exercises in this workout.</p>
                  )}

                  {/* Start workout button */}
                  <button
                    onClick={() => {
                      const today = new Date();
                      // Find the date for this workout's day
                      const weekStart = startOfWeek(today);
                      const workoutJSDay = isoDayToJS(scheduleItem.day_of_week);
                      const days = eachDayOfInterval({ start: weekStart, end: endOfWeek(today) });
                      const workoutDate = days.find(d => d.getDay() === workoutJSDay) || today;
                      navigate(`/programs/${id}/session/${format(workoutDate, 'yyyy-MM-dd')}`);
                    }}
                    className="w-full mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center justify-center space-x-2"
                  >
                    <PlayCircle className="h-5 w-5" />
                    <span>Start Workout</span>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderExerciseList = () => {
    if (!programData?.currentSchedule[0]?.exercise_prescription) return null;

    const prescription = programData.currentSchedule[0].exercise_prescription;

    // Handle modular structure (fitness workouts)
    if (prescription.modules && prescription.modules.length > 0) {
      return (
        <div className="space-y-4">
          {prescription.modules.map((module: any) => {
            const ModuleIcon = getModuleIcon(module.module_type);
            const isExpanded = expandedModules.has(module.module_id);

            return (
              <div key={module.module_id} className="bg-white rounded-lg border">
                <button
                  onClick={() => toggleModule(module.module_id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <ModuleIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium text-gray-900">{module.name || module.module_name}</h4>
                      <p className="text-sm text-gray-600">
                        {module.exercises?.length || 0} exercises
                        {module.duration_minutes && ` • ${module.duration_minutes} min`}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {isExpanded && (
                  <div className="border-t px-4 py-3 space-y-3">
                    {module.instructions && (
                      <p className="text-sm text-gray-600 italic">{module.instructions}</p>
                    )}

                    {module.exercises?.map((exercise: any, index: number) => (
                      <ExerciseDetailRow key={index} exercise={exercise} index={index} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Handle simple exercise list (rehab programs)
    if (prescription.exercises && prescription.exercises.length > 0) {
      return (
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-medium text-gray-900 mb-3">Exercise List</h4>
          <div>
            {prescription.exercises.map((exercise: any, index: number) => (
              <ExerciseDetailRow key={index} exercise={exercise} index={index} />
            ))}
          </div>
        </div>
      );
    }

    return <p className="text-gray-500">No exercises found for this program.</p>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Loading..." showBack />
        <div className="flex items-center justify-center h-64">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!programData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Program Not Found" showBack />
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-600">Unable to load program details.</p>
        </div>
      </div>
    );
  }

  const { assignment, program, progress } = programData;
  const programName = program?.name || 'Exercise Program';
  const isWorkoutPlan = assignment.assignment_type === 'workout_plan';
  const isRehabProgram = assignment.assignment_type === 'rehab_program';

  // Ladder-based plans (plan_type generated | base_routine) get self-service actions
  const isLadderPlan = isWorkoutPlan && (program?.plan_type === 'generated' || program?.plan_type === 'base_routine');
  const isActive = assignment.status === 'active';
  const blockComplete = isWorkoutPlan && (progress?.current_week || 1) >= (program?.duration_weeks || 1);
  const todayISODay = getISODay(new Date());
  const hasWorkoutToday = !isWorkoutPlan
    || !programData.currentSchedule?.length
    || applyScheduleOverrides(programData.currentSchedule, progress?.current_week || 1).some((item: any) => item.day_of_week === todayISODay);

  const runPlanAction = async (action: 'regenerate' | 'advance') => {
    setPlanAction(action);
    setPlanActionError(null);
    try {
      const result = action === 'advance'
        ? await patientApiService.advanceBlock(assignment.id)
        : await patientApiService.generatePlan();
      if (result?.assignment_id) {
        navigate(`/programs/${result.assignment_id}`, { replace: true });
      }
    } catch (error: any) {
      console.error('Plan action failed:', error);
      setPlanActionError(error?.message || 'That did not work. Please try again.');
    } finally {
      setPlanAction(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <PageHeader title={programName} showBack />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Program Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className={`px-3 py-1 text-sm rounded-full ${assignment.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                  }`}>
                  {assignment.status}
                </span>
                {isWorkoutPlan && (
                  <span className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full">
                    Workout Plan
                  </span>
                )}
                {isRehabProgram && (
                  <span className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full">
                    Rehabilitation
                  </span>
                )}
              </div>

              {program?.description && (
                <p className="text-gray-600 mb-4">{program.description}</p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="font-medium">
                    {isWorkoutPlan
                      ? `${program.duration_weeks} weeks`
                      : isRehabProgram
                        ? `${program.expected_duration_weeks} weeks (${program.total_phases} phases)`
                        : 'Ongoing'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Progress</p>
                  <p className="font-medium">
                    {isWorkoutPlan
                      ? `Week ${progress?.current_week || 1} of ${program.duration_weeks}`
                      : isRehabProgram
                        ? `Phase ${progress?.current_phase || 1} of ${program.total_phases}`
                        : `${progress?.sessions_completed || 0} sessions`}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Completion</p>
                  <p className="font-medium">{Math.round(progress?.completion_percentage || 0)}%</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-primary h-3 rounded-full transition-all"
                    style={{ width: `${progress?.completion_percentage || 0}%` }}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate(`/programs/${id}/session/${format(new Date(), 'yyyy-MM-dd')}`)}
              disabled={!hasWorkoutToday}
              title={hasWorkoutToday ? undefined : 'Rest day. Your next workout is on the schedule below.'}
              className="ml-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayCircle className="h-5 w-5" />
              <span>{hasWorkoutToday ? 'Start Today' : 'Rest Day'}</span>
            </button>
          </div>

          {isLadderPlan && isActive && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap gap-3">
                {blockComplete && (
                  <button
                    onClick={() => runPlanAction('advance')}
                    disabled={planAction !== null}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50"
                  >
                    {planAction === 'advance' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                    <span>Start next 2-week block</span>
                  </button>
                )}
                {program?.plan_type === 'base_routine' && (
                  <button
                    onClick={() => navigate(`/programs/${id}/personalize`)}
                    disabled={planAction !== null}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Zap className="h-4 w-4" />
                    <span>Switch to a personalized plan</span>
                  </button>
                )}
                {program?.plan_type === 'generated' && (
                  <button
                    onClick={() => runPlanAction('regenerate')}
                    disabled={planAction !== null}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 ${planAction === 'regenerate' ? 'animate-spin' : ''}`} />
                    <span>Regenerate my plan</span>
                  </button>
                )}
              </div>
              {planAction && (
                <p className="text-sm text-gray-500 mt-2">
                  {planAction === 'advance' ? 'Building your next block...' : 'Building your plan. This takes a few seconds.'}
                </p>
              )}
              {planActionError && (
                <p className="text-sm text-red-600 mt-2">{planActionError}</p>
              )}
            </div>
          )}
        </div>

        {/* View Tabs */}
        <div className="bg-white rounded-lg shadow-sm p-1 mb-6 inline-flex">
          <button
            onClick={() => setCurrentView('overview')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'overview'
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            Overview
          </button>
          <button
            onClick={() => setCurrentView('schedule')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'schedule'
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            Schedule
          </button>
          <button
            onClick={() => setCurrentView('exercises')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'exercises'
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            {isWorkoutPlan ? 'Workouts' : 'Exercises'}
          </button>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {currentView === 'overview' && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold">This Week's Schedule</h3>
                  {isWorkoutPlan && hasScheduleOverrides() && (
                    <button
                      onClick={handleResetSchedule}
                      disabled={isResettingSchedule}
                      className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${isResettingSchedule ? 'animate-spin' : ''}`} />
                      {isResettingSchedule ? 'Resetting...' : 'Reset to Original'}
                    </button>
                  )}
                </div>
                {renderWeekView()}
              </div>

              {isRehabProgram && program && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Program Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Condition</span>
                      <span className="font-medium">{program.condition}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Protocol Source</span>
                      <span className="font-medium">{program.protocol_source}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Current Phase</span>
                      <span className="font-medium">Phase {progress?.current_phase || 1}</span>
                    </div>
                  </div>
                </div>
              )}

              {isWorkoutPlan && program && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Program Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Fitness Level</span>
                      <span className="font-medium capitalize">{program.fitness_level}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Plan Type</span>
                      <span className="font-medium">{program.plan_type}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Current Week</span>
                      <span className="font-medium">Week {progress?.current_week || 1}</span>
                    </div>
                  </div>
                </div>
              )}

              {assignment.notes && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Provider Notes</h3>
                  <p className="text-gray-600">{assignment.notes}</p>
                </div>
              )}
            </div>
          )}

          {currentView === 'schedule' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Weekly Schedule</h3>
              <div className="mb-4">
                {renderWeekView()}
              </div>

              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-3">Scheduled Sessions</h4>
                <div className="space-y-2">
                  {getScheduleDays().map((scheduled, index) => {
                    // Use ISO day names lookup
                    const dayName = ISO_DAY_NAMES[scheduled.day] || `Day ${scheduled.day}`;
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{dayName}</p>
                          <p className="text-sm text-gray-600">
                            {scheduled.prescription?.name || 'Exercise Session'}
                          </p>
                        </div>
                        {scheduled.optional && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                            Optional
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {currentView === 'exercises' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                {isWorkoutPlan ? "This Week's Workouts" : 'Exercise List'}
              </h3>
              {isWorkoutPlan ? renderWorkoutsList() : renderExerciseList()}
            </div>
          )}
        </div>
      </div>

      {/* Day Swap Modal - Only for workout plans */}
      {isWorkoutPlan && swapTargetDay !== null && (
        <DaySwapModal
          isOpen={showDaySwapModal}
          onClose={() => {
            setShowDaySwapModal(false);
            setSwapTargetDay(null);
          }}
          onConfirm={handleDaySwap}
          currentDay={swapTargetDay}
          currentWorkoutName={swapTargetWorkoutName}
          weekNumber={selectedWeek}
          scheduledWorkouts={getScheduledWorkoutsForSwapModal()}
        />
      )}
    </div>
  );
}